import React from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { useLandingTheme } from "../../LandingThemeContext";
import { MagnifierLens } from "../../visuals/MagnifierLens";
import { Handwritten } from "../hero/FieldGuideHero";
import { SECTION_GAP, ThemedBody, ThemedContainer, ThemedDisplayTitle } from "./ThemedLayout";

const MotionBox = motion(Box);

const PLATES = [
  { file: "plate-banyan.png", numeral: "II", name: "Banyan", note: "unlocked at 40 questions" },
  { file: "plate-banana.png", numeral: "III", name: "Banana", note: "the first one you'll grow" },
  { file: "plate-ipe.png", numeral: "IV", name: "Ipê", note: "blooms after a 7-day streak" },
];

// Numbered botanical plates of the trees a student grows. Plate I sits under a hand lens.
export function FieldGuidePlates() {
  const theme = useLandingTheme();
  const reduceMotion = useReducedMotion();
  return (
    <Box as="section" mt={SECTION_GAP} position="relative">
      <ThemedContainer>
        <Flex direction={{ base: "column", lg: "row" }} gap={{ base: "40px", lg: "72px" }} align="flex-start">
          <Box flex="1" minW={0} position="relative">
            <Box position="relative" bg={theme.color.surface} border={`1px solid ${theme.color.border}`} p={{ base: "12px", md: "18px" }} boxShadow="0 24px 48px rgba(18, 48, 31, 0.10)">
              <Box h={{ base: "460px", md: "620px" }} bg="#F7F4EA" position="relative" overflow="hidden">
                <MagnifierLens imageUrl="/images/landing/generated/plate-kapok.png" animate={!reduceMotion} />
              </Box>
              <Flex justify="space-between" align="baseline" pt="14px" px="4px">
                <Text fontFamily={theme.font.body} fontSize="12px" letterSpacing="0.12em" textTransform="uppercase" color={theme.color.muted}>
                  Plate I — Kapok
                </Text>
                <Handwritten fontSize="20px">move the lens</Handwritten>
              </Flex>
            </Box>
            <Handwritten position="absolute" right={{ base: "8px", lg: "-24px" }} top="-36px" transform="rotate(4deg)" fontSize="22px" color={theme.color.muted}>
              Ceiba pentandra
            </Handwritten>
          </Box>

          <Box flex="1" minW={0}>
            <ThemedDisplayTitle>Grow a tree as you learn.</ThemedDisplayTitle>
            <ThemedBody mt="16px" maxW="440px">
              Every answer grows your tree. Finish a deck and it joins your collection — five species so far, each drawn as a plate.
            </ThemedBody>
            <Flex gap={{ base: "12px", md: "18px" }} mt="36px" align="flex-start">
              {PLATES.map((plate, plateIndex) => (
                <MotionBox
                  key={plate.file}
                  flex="1"
                  bg={theme.color.surface}
                  border={`1px solid ${theme.color.border}`}
                  p="10px"
                  initial={{ opacity: 0, y: 24, rotate: plateIndex % 2 === 0 ? -1.5 : 1.5 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -8, rotate: 0, boxShadow: "0 24px 40px rgba(18, 48, 31, 0.16)" }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.5, delay: plateIndex * 0.12 }}
                >
                  <Box bg="#F7F4EA" h={{ base: "150px", md: "210px" }} display="flex" alignItems="flex-end" justifyContent="center" overflow="hidden">
                    <Image loading="lazy" src={`/images/landing/generated/${plate.file}`} alt={`${plate.name} tree, botanical plate`} maxH="100%" objectFit="contain" />
                  </Box>
                  <Text mt="10px" fontFamily={theme.font.body} fontSize="11px" letterSpacing="0.12em" textTransform="uppercase" color={theme.color.muted}>
                    Plate {plate.numeral} — {plate.name}
                  </Text>
                  <Handwritten fontSize="18px" mt="2px" color={theme.color.muted}>
                    {plate.note}
                  </Handwritten>
                </MotionBox>
              ))}
            </Flex>
          </Box>
        </Flex>
      </ThemedContainer>
    </Box>
  );
}
